import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Trash2, Edit3, Plus, X } from "lucide-react";

const empty = {
  slug: "", title: "", subtitle: "", author: "", price: 0, currency: "USD",
  cover_image: "", promo_image: "", short_description: "", long_description: "",
  sample_excerpt: "", pdf_url: "", pages: 0, reading_time_min: 0,
  categories: [], chapters: [], testimonials: [], published: true,
};

export default function Admin() {
  const [books, setBooks] = useState([]);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    const { data } = await api.get("/admin/books");
    setBooks(data.books || []);
  };
  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e.preventDefault();
    const payload = {
      ...editing,
      price: parseFloat(editing.price) || 0,
      pages: parseInt(editing.pages) || 0,
      reading_time_min: parseInt(editing.reading_time_min) || 0,
      categories: (editing.categoriesStr || editing.categories?.join(", ") || "")
        .split(",").map(s => s.trim()).filter(Boolean),
      chapters: (editing.chaptersStr || editing.chapters?.join("\n") || "")
        .split("\n").map(s => s.trim()).filter(Boolean),
    };
    delete payload.categoriesStr; delete payload.chaptersStr;
    try {
      if (editing.id) {
        await api.patch(`/admin/books/${editing.id}`, payload);
        toast.success("Book updated");
      } else {
        await api.post("/admin/books", payload);
        toast.success("Book created");
      }
      setEditing(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this book?")) return;
    await api.delete(`/admin/books/${id}`);
    toast.success("Deleted");
    load();
  };

  return (
    <div className="max-w-[1360px] mx-auto px-6 lg:px-12 pt-16 pb-24">
      <div className="flex items-end justify-between mb-12">
        <div>
          <p className="eyebrow">Admin</p>
          <h1 className="font-serif-display text-5xl leading-none tracking-tighter mt-4">Book Manager</h1>
        </div>
        <button onClick={() => setEditing({ ...empty })} data-testid="admin-new-book" className="btn-primary"><Plus size={14}/> New Book</button>
      </div>

      <div className="hairline mb-8" />

      <table className="w-full text-left">
        <thead>
          <tr className="border-b border-[#E5E2DC]">
            <th className="eyebrow py-3">Title</th>
            <th className="eyebrow py-3">Slug</th>
            <th className="eyebrow py-3">Price</th>
            <th className="eyebrow py-3">Published</th>
            <th className="eyebrow py-3 text-right">Actions</th>
          </tr>
        </thead>
        <tbody>
          {books.map((b) => (
            <tr key={b.id} className="border-b border-[#E5E2DC]" data-testid={`admin-row-${b.slug}`}>
              <td className="py-4 font-serif-display text-lg">{b.title}</td>
              <td className="py-4 text-sm text-[#5C5C5C] font-mono">{b.slug}</td>
              <td className="py-4">${b.price?.toFixed(2)}</td>
              <td className="py-4"><span className={`text-xs uppercase tracking-widest ${b.published ? "text-[#2A3B32]" : "text-[#B85C38]"}`}>{b.published ? "Live" : "Draft"}</span></td>
              <td className="py-4">
                <div className="flex gap-2 justify-end">
                  <button onClick={() => setEditing({ ...b, categoriesStr: b.categories?.join(", "), chaptersStr: b.chapters?.join("\n") })}
                    data-testid={`admin-edit-${b.slug}`} className="p-2 border border-[#E5E2DC] hover:bg-[#F9F8F6]"><Edit3 size={14}/></button>
                  <button onClick={() => del(b.id)} data-testid={`admin-delete-${b.slug}`} className="p-2 border border-[#E5E2DC] hover:bg-[#F9F8F6] text-[#B85C38]"><Trash2 size={14}/></button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {editing && (
        <div className="fixed inset-0 z-[60] bg-black/40 flex items-start justify-center overflow-y-auto p-4">
          <div className="bg-white max-w-2xl w-full my-10 border border-[#E5E2DC]">
            <div className="flex items-center justify-between px-6 py-4 border-b border-[#E5E2DC]">
              <p className="eyebrow">{editing.id ? "Edit book" : "New book"}</p>
              <button onClick={() => setEditing(null)} data-testid="admin-close-modal"><X size={18}/></button>
            </div>
            <form onSubmit={save} className="p-6 space-y-4">
              {["title","subtitle","slug","author"].map(k => (
                <div key={k}>
                  <label className="eyebrow">{k}</label>
                  <input required={k==="title"||k==="slug"} value={editing[k] || ""} onChange={e=>setEditing({...editing, [k]:e.target.value})} data-testid={`admin-field-${k}`}
                    className="mt-1 w-full bg-[#F9F8F6] border border-[#E5E2DC] px-3 py-2 text-sm" />
                </div>
              ))}
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="eyebrow">Price</label>
                  <input type="number" step="0.01" value={editing.price} onChange={e=>setEditing({...editing, price:e.target.value})} className="mt-1 w-full bg-[#F9F8F6] border border-[#E5E2DC] px-3 py-2 text-sm" />
                </div>
                <div>
                  <label className="eyebrow">Pages</label>
                  <input type="number" value={editing.pages} onChange={e=>setEditing({...editing, pages:e.target.value})} className="mt-1 w-full bg-[#F9F8F6] border border-[#E5E2DC] px-3 py-2 text-sm" />
                </div>
                <div>
                  <label className="eyebrow">Read (min)</label>
                  <input type="number" value={editing.reading_time_min} onChange={e=>setEditing({...editing, reading_time_min:e.target.value})} className="mt-1 w-full bg-[#F9F8F6] border border-[#E5E2DC] px-3 py-2 text-sm" />
                </div>
              </div>
              {[["cover_image","Cover image URL"],["promo_image","Promo image URL"],["pdf_url","PDF URL"]].map(([k,l])=>(
                <div key={k}>
                  <label className="eyebrow">{l}</label>
                  <input value={editing[k] || ""} onChange={e=>setEditing({...editing, [k]:e.target.value})} className="mt-1 w-full bg-[#F9F8F6] border border-[#E5E2DC] px-3 py-2 text-sm" />
                </div>
              ))}
              <div>
                <label className="eyebrow">Categories (comma-separated)</label>
                <input value={editing.categoriesStr ?? ""} onChange={e=>setEditing({...editing, categoriesStr:e.target.value})} className="mt-1 w-full bg-[#F9F8F6] border border-[#E5E2DC] px-3 py-2 text-sm" />
              </div>
              <div>
                <label className="eyebrow">Short description</label>
                <textarea rows={2} value={editing.short_description || ""} onChange={e=>setEditing({...editing, short_description:e.target.value})} className="mt-1 w-full bg-[#F9F8F6] border border-[#E5E2DC] px-3 py-2 text-sm" />
              </div>
              <div>
                <label className="eyebrow">Long description</label>
                <textarea rows={5} value={editing.long_description || ""} onChange={e=>setEditing({...editing, long_description:e.target.value})} className="mt-1 w-full bg-[#F9F8F6] border border-[#E5E2DC] px-3 py-2 text-sm" />
              </div>
              <div>
                <label className="eyebrow">Sample excerpt</label>
                <textarea rows={2} value={editing.sample_excerpt || ""} onChange={e=>setEditing({...editing, sample_excerpt:e.target.value})} className="mt-1 w-full bg-[#F9F8F6] border border-[#E5E2DC] px-3 py-2 text-sm" />
              </div>
              <div>
                <label className="eyebrow">Chapters (one per line)</label>
                <textarea rows={5} value={editing.chaptersStr ?? ""} onChange={e=>setEditing({...editing, chaptersStr:e.target.value})} className="mt-1 w-full bg-[#F9F8F6] border border-[#E5E2DC] px-3 py-2 text-sm font-mono" />
              </div>
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={!!editing.published} onChange={e=>setEditing({...editing, published:e.target.checked})} />
                Published
              </label>
              <div className="flex gap-3 pt-4 border-t border-[#E5E2DC]">
                <button type="submit" data-testid="admin-save-book" className="btn-primary">Save</button>
                <button type="button" onClick={()=>setEditing(null)} className="btn-ghost">Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
